import React, { useState, useEffect, useContext, useMemo } from "react";
import LayoutWrapper from "../../components/LayoutWrapper";
import HeaderBar from "../../components/HeaderBar";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import {
  FileText,
  Plus,
  Search,
  Filter,
  Eye,
  Trash2,
  Building2,
} from "lucide-react";

const API = "https://nissi-india.com/api";

const QuotationListing = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branch = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  const loginuserid = Number(userId) || 0;
  const fld_branchid = Number(branchId) || 0;

  const normalizedBranch = String(branch || "").trim().toUpperCase();
  const isHO =
    normalizedBranch === "HO" ||
    normalizedBranch === "HEAD-OFFICE" ||
    normalizedBranch === "HEAD OFFICE";

  const [location, setLocation] = useState(isHO ? "HO" : "BRANCH");
  const [selectedBranch, setSelectedBranch] = useState(isHO ? "" : String(fld_branchid));
  const [branchList, setBranchList] = useState([]);
  const [quotationNo, setQuotationNo] = useState("");
  const [accountApproved, setAccountApproved] = useState("");
  const [status, setStatus] = useState("Approved");
  const [supplier, setSupplier] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [showFilters, setShowFilters] = useState(true);
  const [data, setData] = useState([]);
  const [branchType, setBranchType] = useState(isHO ? "HO" : "Branch");
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  const navigate = useNavigate();

  const inputClass = `w-full px-3 py-2.5 rounded-lg border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-${baseColor}-500/30 focus:border-${baseColor}-500 ${
    isDark
      ? "bg-slate-800/60 border-slate-600/50 text-white placeholder-slate-400"
      : "bg-white border-slate-300 text-slate-900 placeholder-slate-400"
  }`;

  const labelClass = `block text-xs font-semibold tracking-wide mb-1.5 ${
    isDark ? "text-slate-300" : "text-slate-700"
  }`;

  useEffect(() => {
    axios
      .get(`${API}/getRateComparisonBranches`, {
        headers: commonHeaders,
      })
      .then((res) => {
        if (Array.isArray(res.data.data)) {
          setBranchList(res.data.data);
        } else {
          console.error("Expected array at res.data.data but got:", res.data);
          setBranchList([]);
        }
      })
      .catch((err) => {
        toast.error("Failed to load branches");
        console.error(err);
        setBranchList([]);
      });
  }, [commonHeaders]);

  const handleSearch = () => {
    if (!fromDate || !toDate) {
      toast.error("Please select all required fields");
      return;
    }

    const payload = {
      branchid: selectedBranch || fld_branchid || 0,
      branchwiseno: quotationNo,
      fromdate: fromDate,
      todate: toDate,
      purreqno: "",
      branchtype: location === "HO" ? "H" : "B",
      supplier,
      accountApproved,
      status,
      loginuserid,
    };

    setLoading(true);
    axios
      .post(`${API}/getQuotationListingDetails`, payload, {
        headers: commonHeaders,
      })
      .then((res) => {
        const responseData = res.data;
        if (responseData && Array.isArray(responseData.data)) {
          setData(responseData.data);
          toast.success("Data loaded successfully");
        } else {
          console.warn("API did not return an array:", responseData);
          setData([]);
        }
      })
      .catch((err) => {
        toast.error("Failed to fetch quotations");
        console.error(err);
        setData([]);
      })
      .finally(() => setLoading(false));
  };

  const handleBranchTypeChange = (value) => {
    setBranchType(value);

    if (value === "HO") {
      setLocation("HO");
      setSelectedBranch("");
    } else {
      setLocation("BRANCH");
      setSelectedBranch(String(fld_branchid || ""));
    }
  };

  const clearFilters = () => {
    setQuotationNo("");
    setAccountApproved("");
    setStatus("Approved");
    setSupplier("");
    setFromDate("");
    setToDate("");
    setSelectedBranch(isHO ? "" : String(fld_branchid || ""));
  };

  const getApprovalBadge = (value) => {
    if ((value || "") === "Approved") return "bg-green-100 text-green-800";
    return "bg-yellow-100 text-yellow-800";
  };

  // ── Get Quotation ID from row ──────────────────────────────
  const getQuotationId = (item) =>
    item.fld_quotationid ||
    item.quotationId ||
    item.fld_Quotationid ||
    item.id ||
    item.quotationid ||
    null;

  // ── View / Edit ────────────────────────────────────────────
  const handleView = (item) => {
    const quotationId = getQuotationId(item);

    if (!quotationId) {
      toast.error("Quotation ID not found for this row");
      console.log("Row data:", item);
      return;
    }

    navigate(`/inventory/quotation-entry-gst?quotationId=${quotationId}`);
  };

  // ── Delete single ──────────────────────────────────────────
  const handleDelete = async (item) => {
    const quotationId = getQuotationId(item);

    if (!quotationId) {
      toast.error("Quotation ID not found for this row");
      console.log("Row data:", item);
      return;
    }

    if (!window.confirm("Are you sure you want to delete this quotation?")) {
      return;
    }

    try {
      const res = await axios.delete(`${API}/deleteQuotation/${quotationId}`, {
        headers: commonHeaders,
      });

      if (res.data?.success !== false) {
        toast.success(res.data?.message || "Quotation deleted successfully");
        handleSearch(); // refresh list
      } else {
        toast.error(res.data?.message || "Delete failed");
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to delete quotation");
    }
  };

  // ── Checkbox select ────────────────────────────────────────
  const handleCheckboxChange = (id) => {
    if (!id) return;
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  return (
    <LayoutWrapper>
      <HeaderBar
        title={<span className="font-dmserif text-3xl">Rate Comparison Listing</span>}
        setSkin={setSkin}
        currentSkin={skin}
      />

      <div className="space-y-6 mt-6">
        {/* Header Card */}
        <div
          className={`p-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `bg-white ${currentSkin.glass} text-black border-gray-200`
          }`}
        >
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <div
                className={`flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
              >
                <FileText className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">Rate Comparison Records</h1>
                <p className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Search and manage rate comparison quotations
                </p>
              </div>
              <span
                className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  isDark ? "bg-gray-700 text-gray-300" : "bg-gray-100 text-gray-600"
                }`}
              >
                {data.length} records
              </span>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
                  isDark
                    ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                    : "border-slate-300 text-slate-700 hover:bg-slate-100"
                } ${showFilters ? (isDark ? "bg-slate-800" : "bg-slate-100") : ""}`}
              >
                <Filter size={16} />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button
                onClick={() => navigate("/inventory/RateComparison")}
                className={`inline-flex items-center gap-2 px-4 py-2.5 text-white text-sm font-semibold rounded-xl shadow transition ${buttonClass} ${hoverClass}`}
              >
                <Plus size={16} />
                Add New
              </button>
            </div>
          </div>

          {/* Branch Type Toggle */}
          <div>
            <label className={labelClass}>Branch Type</label>
            <div className="flex gap-2">
              <button
                onClick={() => handleBranchTypeChange("HO")}
                disabled={!isHO}
                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition ${
                  branchType === "HO"
                    ? `${buttonClass} ${hoverClass} text-white shadow`
                    : isDark
                    ? "bg-slate-800 text-slate-300 border border-slate-600"
                    : "bg-gray-100 text-gray-700 border border-gray-200"
                } ${!isHO ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                <Building2 size={16} />
                Head Office
              </button>

              <button
                onClick={() => handleBranchTypeChange("Branch")}
                disabled={isHO}
                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition ${
                  branchType === "Branch"
                    ? "bg-blue-600 text-white shadow"
                    : isDark
                    ? "bg-slate-800 text-slate-300 border border-slate-600"
                    : "bg-gray-100 text-gray-700 border border-gray-200"
                } ${isHO ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                Branch
              </button>
            </div>
          </div>
        </div>

        {/* Filters Card */}
        {showFilters && (
          <div
            className={`rounded-2xl shadow-xl border overflow-hidden ${
              isDark
                ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
                : `bg-white ${currentSkin.glass} border-gray-200`
            }`}
          >
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}></div>
                <h3 className="text-lg font-bold">Search Filters</h3>
              </div>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                <div>
                  <label className={labelClass}>
                    From Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>
                    To Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>Quotation No</label>
                  <input
                    type="text"
                    value={quotationNo}
                    onChange={(e) => setQuotationNo(e.target.value)}
                    placeholder="Enter Quotation Number"
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>Branch</label>
                  <select
                    value={selectedBranch}
                    onChange={(e) => setSelectedBranch(e.target.value)}
                    className={inputClass}
                  >
                    <option value="">Select Branch</option>
                    {branchList.map((item, index) => (
                      <option key={index} value={item.fld_branchid}>
                        {item.fld_branchname}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Supplier</label>
                  <select
                    value={supplier}
                    onChange={(e) => setSupplier(e.target.value)}
                    className={inputClass}
                  >
                    <option value="">All Suppliers</option>
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Account Approved</label>
                  <input
                    type="text"
                    value={accountApproved}
                    onChange={(e) => setAccountApproved(e.target.value)}
                    placeholder="Enter Account Approved"
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>Status</label>
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    className={inputClass}
                  >
                    <option value="">All Status</option>
                    <option value="Approved">Approved</option>
                    <option value="Pending">Pending</option>
                  </select>
                </div>
              </div>

              <div className="flex flex-wrap justify-end gap-3 mt-6 pt-5 border-t border-slate-200 dark:border-slate-700">
                <button
                  onClick={clearFilters}
                  className={`px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
                    isDark
                      ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                      : "border-slate-300 text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  Clear Filters
                </button>

                <button
                  onClick={handleSearch}
                  disabled={loading}
                  className={`inline-flex items-center gap-2 px-5 py-2.5 text-white text-sm font-semibold rounded-xl shadow transition ${buttonClass} ${hoverClass} disabled:opacity-50`}
                >
                  <Search size={16} />
                  {loading ? "Searching..." : "Search Records"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Results Table Card */}
        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}></div>
              <h3 className="text-xl font-bold">Quotation List</h3>
            </div>
          </div>

          {data.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead
                  className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}
                >
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">S.No</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">Request No/From Site</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">Quotation No</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">Requested For</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">Request Approval</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">Quotation Approval</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">Account Approval</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">MD Approval</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">Status</th>
                    <th className="px-4 py-3 text-center text-xs font-bold uppercase">Actions</th>
                  </tr>
                </thead>

                <tbody className={`divide-y ${isDark ? "divide-gray-600" : "divide-gray-200"}`}>
                  {data.map((item, index) => {
                    const quotationId = getQuotationId(item);
                    const isSelected = selectedIds.includes(quotationId);

                    return (
                      <tr
                        key={quotationId || index}
                        className={`transition ${
                          isDark
                            ? "bg-gray-800/20 hover:bg-gray-700/30"
                            : "bg-white hover:bg-gray-50"
                        } ${isSelected ? `bg-${baseColor}-50 dark:bg-${baseColor}-900/20` : ""}`}
                      >
                        <td className="px-4 py-3 font-medium">{index + 1}</td>
                        <td className="px-4 py-3 font-medium">
                          {item.requestNo || item.fld_requestNo || "-"}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${
                              isDark
                                ? "bg-gray-700 text-gray-200"
                                : `bg-${baseColor}-100 text-${baseColor}-800`
                            }`}
                          >
                            {item.quotationNo || item.fld_QuotationNo || "-"}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {item.date || item.fld_QuotationDate || "-"}
                        </td>
                        <td className="px-4 py-3">
                          {item.requestedFor || item.fld_req_for || "-"}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${getApprovalBadge(
                              item.requestApproval
                            )}`}
                          >
                            {item.requestApproval || "-"}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${getApprovalBadge(
                              item.quotationApproval
                            )}`}
                          >
                            {item.quotationApproval || "-"}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${getApprovalBadge(
                              item.accountApproval
                            )}`}
                          >
                            {item.accountApproval || "-"}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${getApprovalBadge(
                              item.mdApproval
                            )}`}
                          >
                            {item.mdApproval || "-"}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${getApprovalBadge(
                              item.status
                            )}`}
                          >
                            {item.status || "-"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <input
                              type="checkbox"
                              className="w-4 h-4 rounded"
                              checked={isSelected}
                              onChange={() => handleCheckboxChange(quotationId)}
                            />

                            {/* View / Edit */}
                            <button
                              onClick={() => handleView(item)}
                              className="p-1.5 rounded-full bg-blue-100 text-blue-600 hover:bg-blue-200 transition"
                              title="View / Edit"
                            >
                              <Eye size={16} />
                            </button>

                            {/* Delete */}
                            <button
                              onClick={() => handleDelete(item)}
                              className="p-1.5 rounded-full bg-red-100 text-red-600 hover:bg-red-200 transition"
                              title="Delete"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16">
              <FileText size={48} className="text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">
                No Records Found
              </h3>
              <p className="text-gray-500 text-center max-w-md text-sm">
                Try adjusting your search filters or add a new rate comparison record.
              </p>
            </div>
          )}
        </div>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />
    </LayoutWrapper>
  );
};

export default QuotationListing;